import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { isOwner } from "@/lib/roles";
import { Mail, Send, X, AlertTriangle, CheckCircle2, XCircle, MinusCircle } from "lucide-react";
import { EmailedChip, wasEmailed } from "@/components/EmailedBadge";

const MAX = 100;
const CHUNK = 5;
const DAILY = "Gmail's daily sending limit";
const inputCls = "w-full border border-[#15233B]/15 rounded px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E73C9]/30";
const labelCls = "text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60";
const hasEmail = (c) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((c.email || "").trim());

function Summary({ results, onClose, onRetry }) {
  const by = (s) => results.filter((r) => r.status === s);
  const icon = { sent: CheckCircle2, failed: XCircle, skipped: MinusCircle };
  const color = { sent: "text-[#166534]", failed: "text-[#b91c1c]", skipped: "text-[#15233B]/50" };
  return (
    <div data-testid="bulk-email-summary" className="p-5 space-y-3">
      <div className="flex gap-3 text-sm font-bold">
        <span data-testid="bulk-email-sent-count" className="text-[#166534]">{by("sent").length} sent</span>
        <span data-testid="bulk-email-failed-count" className="text-[#b91c1c]">{by("failed").length} failed</span>
        <span data-testid="bulk-email-skipped-count" className="text-[#15233B]/50">{by("skipped").length} skipped</span>
      </div>
      {[...new Set(by("failed").map((r) => r.error))].slice(0, 2).map((reason) => (
        <div key={reason} data-testid="bulk-email-fail-reason" className="text-xs text-[#b91c1c] bg-[#b91c1c]/5 border border-[#b91c1c]/20 rounded px-3 py-2 break-words">{reason}</div>
      ))}
      <div className="max-h-72 overflow-y-auto divide-y divide-[#15233B]/5 border border-[#15233B]/10 rounded">
        {results.map((r) => {
          const I = icon[r.status];
          return (
            <div key={r.card_id} className="flex items-center gap-2 px-3 py-1.5 text-xs">
              <I className={`h-3.5 w-3.5 ${color[r.status]}`} />
              <span className="font-semibold text-[#15233B]">{r.name || r.card_id}</span>
              {r.error && <span className="ml-auto text-[#15233B]/50 truncate">{r.error}</span>}
            </div>
          );
        })}
      </div>
      <div className="flex justify-end gap-2">
        {by("failed").length > 0 && (
          <button data-testid="bulk-email-retry" onClick={onRetry} className="px-4 py-2 rounded border border-[#1E73C9]/40 text-[#1E73C9] text-sm font-bold hover:bg-[#1E73C9]/10">Retry failed ({by("failed").length})</button>
        )}
        <button data-testid="bulk-email-done" onClick={onClose} className="px-4 py-2 rounded bg-[#15233B] text-white text-sm font-bold hover:bg-[#1E73C9]">Done</button>
      </div>
    </div>
  );
}

const NO_JOB = "No job listed";
const JOB_ALIASES = { "personal care worker": "PCW", "certified nursing assistant": "CNA", "registered nurse": "RN", "licensed practical nurse": "LPN" };
const jobLabel = (pos) => {
  const t = (pos || "").trim();
  if (!t) return NO_JOB;
  return JOB_ALIASES[t.toLowerCase()] || (t.length <= 4 ? t.toUpperCase() : t);
};
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function Recipient({ c, checked, busy, before, stage, onToggle }) {
  const ok = hasEmail(c);
  return (
    <label data-testid={`bulk-email-recipient-${c.id}`} className={`flex items-center gap-2 pl-6 pr-3 py-2 text-xs ${ok ? "cursor-pointer hover:bg-[#FFF9C4]/40" : "opacity-50"}`}>
      <input type="checkbox" data-testid={`bulk-email-check-${c.id}`} disabled={!ok || busy} checked={checked} onChange={() => onToggle(c.id)} className="accent-[#1E73C9]" />
      <div className="min-w-0 flex-1">
        <div className="font-bold text-[#15233B] truncate">{c.full_name} <span className="font-normal text-[#15233B]/45">{c.av_id}</span></div>
        <div className="text-[#15233B]/55 truncate">{ok ? c.email : "No email address"}{stage ? ` · ${stage}` : ""}</div>
      </div>
      <EmailedChip card={c} testId={`bulk-email-emailed-${c.id}`} />
      {before && <span data-testid={`bulk-email-sent-before-${c.id}`} className="text-[9px] font-extrabold uppercase tracking-[0.1em] px-1.5 py-0.5 rounded bg-[#166534]/10 text-[#166534] shrink-0">This template</span>}
    </label>
  );
}

function JobGroup({ job, list, sel, busy, tplId, stageName, canPick, onToggle, onToggleGroup }) {
  const ids = list.filter(canPick).map((c) => c.id);
  const repeats = list.filter((c) => hasEmail(c) && !canPick(c)).length;
  const picked = ids.filter((id) => sel.has(id)).length;
  const all = ids.length > 0 && picked === ids.length;
  const gid = slug(job);
  return (
    <div data-testid={`bulk-email-group-${gid}`} className="border-b border-[#15233B]/10 last:border-b-0">
      <label className={`sticky top-0 z-[1] flex items-center gap-2 px-3 py-1.5 bg-[#F6F3EC] text-[11px] ${ids.length ? "cursor-pointer" : "opacity-60"}`}>
        <input type="checkbox" data-testid={`bulk-email-group-check-${gid}`} disabled={!ids.length || busy} checked={all}
          ref={(el) => { if (el) el.indeterminate = picked > 0 && !all; }} onChange={() => onToggleGroup(list)} className="accent-[#1E73C9]" />
        <span className="font-extrabold uppercase tracking-[0.12em] text-[#15233B]">{job}</span>
        <span data-testid={`bulk-email-group-count-${gid}`} className="ml-auto text-[#15233B]/50 font-semibold">{picked}/{ids.length} selected{repeats ? ` · ${repeats} already emailed` : ""}{list.filter((c) => !hasEmail(c)).length ? ` · ${list.filter((c) => !hasEmail(c)).length} no email` : ""}</span>
      </label>
      <div className="divide-y divide-[#15233B]/5">
        {list.map((c) => (
          <Recipient key={c.id} c={c} checked={sel.has(c.id)} busy={busy} stage={stageName(c.current_stage)}
            before={!!tplId && (c.sent_template_ids || []).includes(tplId)} onToggle={onToggle} />
        ))}
      </div>
    </div>
  );
}

export default function BulkEmailDialog({ cards, workspace, templates: stageTemplates, title, preselected, onClose, onDone }) {
  const { user } = useAuth();
  const [tpls, setTpls] = useState([]);
  const [status, setStatus] = useState(null);
  const [tplId, setTplId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [includeEmailed, setIncludeEmailed] = useState(false);
  const canPick = (c) => hasEmail(c) && (includeEmailed || !wasEmailed(c));
  const [sel, setSel] = useState(() => new Set((preselected || []).filter((id) => { const c = cards.find((x) => x.id === id) || {}; return hasEmail(c) && !wasEmailed(c); })));
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);

  useEffect(() => {
    api.get("/email-templates", { params: { workspace } }).then(({ data }) => setTpls(data || [])).catch(() => setTpls([]));
    api.get("/email/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ connected: false }));
  }, [workspace]);

  const sorted = useMemo(() => [...cards].sort((a, b) => Number(hasEmail(b)) - Number(hasEmail(a)) || a.full_name.localeCompare(b.full_name)), [cards]);
  const emailable = sorted.filter(canPick);
  const repeatCount = sorted.filter((c) => hasEmail(c) && wasEmailed(c)).length;
  const flipInclude = () => {
    if (includeEmailed) setSel((p) => new Set([...p].filter((id) => !wasEmailed(cards.find((c) => c.id === id) || {}))));
    setIncludeEmailed(!includeEmailed);
  };
  const groups = useMemo(() => {
    const m = new Map();
    sorted.forEach((c) => { const k = jobLabel(c.position); if (!m.has(k)) m.set(k, []); m.get(k).push(c); });
    return [...m.entries()].sort(([a], [b]) => (a === NO_JOB) - (b === NO_JOB) || a.localeCompare(b));
  }, [sorted]);
  const toggleGroup = (list) => setSel((p) => {
    const ids = list.filter(canPick).map((c) => c.id);
    const n = new Set(p);
    if (ids.every((id) => n.has(id))) { ids.forEach((id) => n.delete(id)); return n; }
    for (const id of ids) { if (n.size >= MAX) { toast.error(`Up to ${MAX} per send`); break; } n.add(id); }
    return n;
  });
  const stageName = (n) => stageTemplates?.find((t) => t.stage_number === n)?.name || "";
  const toggle = (id) => setSel((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else if (n.size < MAX) n.add(id); else toast.error(`Up to ${MAX} per send`); return n; });
  const pickTemplate = (id) => { setTplId(id); const t = tpls.find((x) => x.id === id); if (t) { setSubject(t.subject); setBody(t.body); } };

  const nameOf = (id) => cards.find((c) => c.id === id)?.full_name;
  const runSend = async (ids, previous = []) => {
    const out = [];
    setResults(null);
    setProgress({ done: 0, total: ids.length });
    for (let i = 0; i < ids.length; i += CHUNK) {
      const chunk = ids.slice(i, i + CHUNK);
      let stop = null;
      try {
        const { data } = await api.post("/cards/bulk-email", { card_ids: chunk, subject, body, template_id: tplId || null });
        out.push(...data.results);
        const daily = data.results.find((r) => (r.error || "").startsWith(DAILY));
        if (daily) stop = daily.error;
      } catch (e) {
        const msg = formatApiError(e);
        chunk.forEach((id) => out.push({ card_id: id, name: nameOf(id), status: "failed", error: msg }));
        if (e.response?.status === 400) stop = "Not sent";
      }
      setProgress({ done: Math.min(i + CHUNK, ids.length), total: ids.length });
      if (stop) { ids.slice(i + CHUNK).forEach((id) => out.push({ card_id: id, name: nameOf(id), status: "failed", error: stop })); break; }
    }
    const redone = new Set(ids);
    setProgress(null);
    setResults([...previous.filter((r) => !redone.has(r.card_id)), ...out]);
    onDone?.();
  };
  const send = () => {
    const ids = Array.from(sel);
    if (!window.confirm(`Send this email to ${ids.length} ${ids.length === 1 ? "person" : "people"}? Each gets their own copy.`)) return;
    runSend(ids);
  };
  const retryFailed = () => {
    const ids = results.filter((r) => r.status === "failed").map((r) => r.card_id);
    if (ids.length) runSend(ids, results);
  };

  const busy = !!progress;
  return (
    <div className="fixed inset-0 z-[60] bg-[#15233B]/50 flex items-center justify-center p-4" onClick={busy ? undefined : onClose}>
      <div data-testid="bulk-email-dialog" onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="px-5 py-4 flex items-center justify-between bg-[#15233B] text-white rounded-t-xl">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-[#FFCB05]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#FFCB05]/90">Bulk email</div>
              <h2 className="text-base font-extrabold leading-tight">{title}</h2>
            </div>
          </div>
          {!busy && <button data-testid="bulk-email-close" onClick={onClose} className="p-1.5 rounded hover:bg-white/10"><X className="h-4 w-4" /></button>}
        </div>
        {results ? <Summary results={results} onClose={onClose} onRetry={retryFailed} /> : (
          <>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5 overflow-y-auto">
              <div className="space-y-3">
                {status && !status.connected && (
                  <div data-testid="bulk-email-not-connected" className="flex gap-2 text-xs text-[#7a5a1a] bg-[#FFF9C4] border border-[#FFCB05]/50 rounded px-2.5 py-2">
                    <AlertTriangle className="h-4 w-4 shrink-0" /> <span>Email isn't connected yet. {isOwner(user) ? <a data-testid="gmail-setup-link" href="/settings?tab=integrations" className="font-bold underline">Set up Gmail now →</a> : "Ask an owner to set up Gmail in Settings → Integrations."}</span>
                  </div>
                )}
                <div>
                  <label className={labelCls}>Template</label>
                  <select data-testid="bulk-email-template" value={tplId} onChange={(e) => pickTemplate(e.target.value)} className={inputCls}>
                    <option value="">— write my own —</option>
                    {tpls.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Subject</label>
                  <input data-testid="bulk-email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Message</label>
                  <textarea data-testid="bulk-email-body" rows={10} value={body} onChange={(e) => setBody(e.target.value)} className={inputCls} />
                </div>
                <div className="text-[10px] text-[#15233B]/50">{"{first_name}"}, {"{full_name}"}, {"{position}"} and {"{my_name}"} are filled in for each person. Everyone gets their own email.</div>
              </div>
              <div className="flex flex-col min-h-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <label className={labelCls}>Send to</label>
                  <span data-testid="bulk-email-selected-count" className="text-[11px] font-bold text-[#1E73C9]">{sel.size} selected</span>
                  <button data-testid="bulk-email-select-all" onClick={() => setSel(new Set(emailable.slice(0, MAX).map((c) => c.id)))} className="ml-auto text-[11px] font-bold text-[#1E73C9] hover:underline">All with email</button>
                  <button data-testid="bulk-email-select-none" onClick={() => setSel(new Set())} className="text-[11px] font-bold text-[#15233B]/50 hover:underline">None</button>
                </div>
                {repeatCount > 0 && (
                  <label data-testid="bulk-email-include-emailed-row" className="flex items-center gap-2 mb-1.5 px-2.5 py-1.5 rounded bg-[#1E73C9]/5 border border-[#1E73C9]/15 text-[11px] text-[#15233B]/75 cursor-pointer">
                    <input type="checkbox" data-testid="bulk-email-include-emailed" checked={includeEmailed} disabled={busy} onChange={flipInclude} className="accent-[#1E73C9]" />
                    <span>Include already emailed <b data-testid="bulk-email-repeat-count">({repeatCount})</b>{includeEmailed ? "" : " — they're skipped so nobody gets a repeat"}</span>
                  </label>
                )}
                <div data-testid="bulk-email-recipients" className="border border-[#15233B]/10 rounded overflow-y-auto max-h-[360px]">
                  {groups.map(([job, list]) => (
                    <JobGroup key={job} job={job} list={list} sel={sel} busy={busy} tplId={tplId} stageName={stageName} canPick={canPick} onToggle={toggle} onToggleGroup={toggleGroup} />
                  ))}
                  {sorted.length === 0 && <div className="px-3 py-6 text-center text-xs italic text-[#15233B]/45">Nobody here.</div>}
                </div>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-[#15233B]/10 flex items-center gap-3">
              {progress && (
                <div className="flex-1" data-testid="bulk-email-progress">
                  <div className="text-[11px] font-bold text-[#15233B]/70 mb-1">Sending {progress.done} of {progress.total}…</div>
                  <div className="h-2 rounded-full bg-[#15233B]/10 overflow-hidden"><div className="h-full bg-[#FFCB05] transition-[width] duration-300" style={{ width: `${(progress.done / progress.total) * 100}%` }} /></div>
                </div>
              )}
              <div className="ml-auto flex gap-2">
                {!busy && <button onClick={onClose} className="px-3 py-2 rounded text-sm font-semibold text-[#15233B] hover:bg-[#15233B]/5">Cancel</button>}
                <button data-testid="bulk-email-send" onClick={send} disabled={busy || !sel.size || !subject.trim() || !body.trim()}
                  className="flex items-center gap-1.5 px-4 py-2 rounded bg-[#FFCB05] text-[#15233B] text-sm font-extrabold hover:bg-[#FBC02D] disabled:opacity-50 transition-colors">
                  <Send className="h-4 w-4" /> Send to {sel.size}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
